/**
 * ANY USED FUNCTION ALONG ONE OR MORE FILES THAT NO DEPENDS ON REACT NOR THE CONTEXT
 */
import {
	LocalStorageKey,
	NumFormat,
	DateFormat,
	PriceRule,
	PriceRuleMatchCriteria,
	PriceRuleMatch,
	TranslationKey,
	BookingData,
	BookingSummary,
	EnvKey,
	TableObject,
	Rect,
	Point,
	RectKey,
	Rects,
	Product,
	SafeZone,
	Journeys,
	VenueData,
	Stage,
	BookingStep,
	Shortcut,
	SafeVenueData,
	ProcessedTableLayout,
	VenueId,
	Shift,
	ComputedShiftData,
	ScoreMaxes,
	Phone
} from '../types'
import {
	MONTH_NAMES_LONG,
	MONTH_NAMES_SHORT,
	DAY_NAMES_SHORT,
	ZONE_NAMES,
	DAY_NAMES_LONG,
	CHILDREN_PER_ADULT,
	TABLES_OVERRIDES,
	booking,
} from './symbols'
import { rumba } from './symbols'
import { translator } from './symbols'

export const log = (...data: any) => {
	if (!rumba.isProd()) console.log(...data)
}

export const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const removeDuplicates = (array: Array<number | string>) => Array.from(new Set(array))

export const load = <T>(from: 'localStorage' | 'sessionStorage', key: LocalStorageKey, defaultValue: T): T => {
    
    try { 

        const storedValue = from === 'sessionStorage' ? 
			sessionStorage.getItem(key) :
			localStorage.getItem(key)

        if (storedValue === null) return defaultValue
        
        const parsedValue = JSON.parse(storedValue);

		// if parsedValue & defaultValue are objects perform a safe load
		if (
			typeof defaultValue === 'object' &&
			!Array.isArray(defaultValue) &&
			defaultValue !== null &&
			typeof parsedValue === 'object' &&
			!Array.isArray(parsedValue) &&
			parsedValue !== null
		) {
			return {
				...defaultValue,
				...parsedValue,
			}
		}
		return parsedValue
	} catch (error) {
		return defaultValue
	}
}

export const deleteItem = (key: LocalStorageKey) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
}

export const loadEnvValue = (key: EnvKey): string => {
	const value = process.env[key]
	if (value === undefined) throw new Error(`${key} not found`)
	return value
}

export const save = (on: 'sessionStorage' | 'localStorage', key:LocalStorageKey, value: object | string | number) => on === 'sessionStorage' ? 
	sessionStorage.setItem(key, JSON.stringify(value)) :
	localStorage.setItem(key, JSON.stringify(value))

export const _ = (translationKey: TranslationKey, ...replace: Array<number | string>): string => {
	try {
		const translation = translator.getProcessedTranslation(translationKey, ...replace)
		if (!translation) throw new Error(`Unknown translation ${translationKey}`)
		return translation
	} catch (error) {
		log(error)
		return translationKey
	}
}

export const calcDiscount = (price: number, discount: number) => {
    if (!discount) return price
    return Math.round(price - (discount > -1 && discount < 1 ? price * discount : discount))
}

export const fixDecimals = (num: number, decimalsQty: number = 2) => Number(num.toFixed(decimalsQty))

export const parseNum = (format: NumFormat, num: number): string => {
	let parsedNum = ''

	switch (format) {
		case '2-digit':
			parsedNum = `${num < 0 ? '-' : ''}${num < 10 ? '0' : ''}${Math.floor(num)}`
			break
		case 'EUR':
			parsedNum = new Intl.NumberFormat('es-ES', {
				style: 'currency',
				currency: 'EUR',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			})
				.format(num)
				.replace(/\u00A0/g, '')
			break
		case 'percent':
			parsedNum = new Intl.NumberFormat('es-ES', {
				style: 'percent',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}).format(num)
			break
		case 'es':
			parsedNum = new Intl.NumberFormat('es-ES', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}).format(num)
			break
		case 'discount':
			parsedNum = Math.abs(num) < 1 ? parseNum('percent', -1 * num) : parseNum('EUR', -1 * num)
			break
		default:
			parsedNum = String(num)
	}

	return parsedNum
}

export const getZoneName = (type: 'long' | 'short', zone: SafeZone, journey: Journeys) => {
	let result = ''
	switch (zone) {
		case 'za':
		case 'za2':
		case 'zb':
		case 'zc':
		case 'zv':
			if (type === 'long') result = `${_('0ge_zone___')} ${ZONE_NAMES[zone]}`
			else result = ZONE_NAMES[zone]
			break
		default:
			break
	}

	return result
}

export const parseDate = (format: DateFormat, date: string | number | Date, utc?: boolean): string => {
	try {
		const dateObj: Date = new Date(date)
		let parsedDate = ''
		if (isNaN(dateObj.getTime())) throw new Error(`Not possible to build a date object with ${date}`)

		const WEEK_DAY = utc ? dateObj.getUTCDay() : dateObj.getDay()
		const DATE = utc ? dateObj.getUTCDate() : dateObj.getDate()
		const MONTH = utc ? dateObj.getUTCMonth() : dateObj.getMonth()
		const YEAR = utc ? dateObj.getUTCFullYear() : dateObj.getFullYear()
		const HOURS = utc ? dateObj.getUTCHours() : dateObj.getHours()
		const MINUTES = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes()
		const SECONDS = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes()

		switch (format) {
			case 'Mon':
				parsedDate = DAY_NAMES_SHORT[WEEK_DAY]
				break
			case 'd':
				parsedDate = String(DATE)
				break
			case 'November yyyy':
				parsedDate = `${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case 'November':
				parsedDate = `${MONTH_NAMES_LONG[MONTH]}`
				break
			case 'Nov':
				parsedDate = MONTH_NAMES_SHORT[MONTH]
				break
			case 'd Nov yyyy':
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} ${YEAR}`
				break
			case 'Mon d November yyyy':
				parsedDate = `${DAY_NAMES_SHORT[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case 'Monday d November yyyy':
				parsedDate = `${DAY_NAMES_LONG[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case "d Nov 'yy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} '${String(YEAR).substring(2)}`
				break
			case 'yyyy-mm-dd':
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(2, '0')}-${String(DATE).padStart(2, '0')}`
				break
			case 'yyyy-mm-dd hh:mm:ss':
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(2, '0')}-${String(DATE).padStart(2, '0')} ${String(
					HOURS
				).padStart(2, '0')}:${String(MINUTES).padStart(2, '0')}:${String(SECONDS).padStart(2, '0')}`
				break
			case 'hh:mm':
				parsedDate = `${String(HOURS).padStart(2, '0')}:${String(MINUTES).padStart(2, '0')}`
				break
			default:
				break
		}

		return parsedDate
	} catch (error) {
		log(error)
		return 'Invalid Date'
	}
}

export const getWeekSlotsTillMonthEnd = (date: Date, firstDayOfWeek?: number): number => {
	const currentDay = date.getDate()
	const firstWeekDay = getAdjustedDay(date)
	const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
	const lastWeekDay = getAdjustedDay(lastDay)
	const remainingDays = lastDay.getDate() - currentDay + 1
	const totalWeekSlots = remainingDays + firstWeekDay + (6 - lastWeekDay)
	return Math.ceil(totalWeekSlots / 7)
}

export const getMonthDifference = (
	date1: string | number | Date | undefined,
	date2: string | number | Date | undefined
): number => {
	if (date1 === undefined || date2 === undefined) return 0
	const firstDate = new Date(date1)
	const secondDate = new Date(date2)
	if (isNaN(firstDate.getTime()) || isNaN(secondDate.getTime()) || date1 === 0 || date2 === 0) return 0
	const year1 = firstDate.getFullYear()
	const month1 = firstDate.getMonth()
	const year2 = secondDate.getFullYear()
	const month2 = secondDate.getMonth()
	const yearDifference = year2 - year1
	const monthDifference = month2 - month1
	return yearDifference * 12 + monthDifference
}

export const getAdjustedDay = (date: Date, firstDayOfWeek?: number | undefined): number =>
	(date.getDay() - (firstDayOfWeek ?? 1) + 7) % 7

export const priceRulesSort = (a: PriceRule, b: PriceRule, journey: Journeys) => {
	if (a.journey === 'B' && journey === 'B') return -1
	if (b.journey === 'B' && journey === 'B') return 1

	if (!!a.cumulative) return -1
	if (!!b.cumulative) return 1

	if (a.priority < b.priority) return -1
	if (a.priority > b.priority) return 1

	return 0
}

export const findPriceRuleMatches = (
	criteria: PriceRuleMatchCriteria,
	priceRules: PriceRule[],
): PriceRuleMatch[] => {
	let now = new Date()
	let bookingDate = new Date(criteria.bookingDate)

	const fulfilledPriceRules: PriceRule[] = []
	const BOOKING_DATE_ISO = parseDate('yyyy-mm-dd', bookingDate)
	const WEEK_DAY = bookingDate.getDay()
	const HOUR = now.getHours()
	const DAYS_TILL_EVENT = Math.ceil((bookingDate.getTime() - now.getTime()) / 1000 / 60 / 60 / 24)
	const SHIFT_ID = criteria.shiftId ?? null
	const EVENT_ID = criteria.eventId ?? null
	const PRODUCT_ID = criteria.productId ?? null

	for (let priceRule of priceRules) {
		const safe_availableFrom = new Date(parseDate('yyyy-mm-dd', priceRule.availableFrom, true))
		const safe_availableTo = new Date(parseDate('yyyy-mm-dd', priceRule.availableTo, true))

		if (safe_availableFrom.getTime() > bookingDate.getTime()) {
			continue
		}
		if (safe_availableTo.getTime() < bookingDate.getTime()) {
			continue
		}
		if (priceRule.journey === 'B' && criteria.journey !== 'B') {
			continue
		}
		if (priceRule.web === 0) {
			continue
		}
		if (priceRule.venueId !== 0 && priceRule.venueId !== rumba.venueId) {
			continue
		}
		if (priceRule.rules.noWeekDays && priceRule.rules.noWeekDays.indexOf(WEEK_DAY) !== -1) {
			continue
		}
		if (DAYS_TILL_EVENT > priceRule.maxDaysBefore || DAYS_TILL_EVENT < priceRule.daysInAdvance) {
			continue
		}
		if (DAYS_TILL_EVENT === priceRule.daysInAdvance && priceRule.maxHour < HOUR) {
			continue
		}
		if (SHIFT_ID && priceRule.rules.yesShiftIds && priceRule.rules.yesShiftIds.indexOf(SHIFT_ID) === -1) {
			continue
		}
		if (EVENT_ID && priceRule.rules.yesEventIds && priceRule.rules.yesEventIds.indexOf(EVENT_ID) === -1) {
			continue
		}
		if (PRODUCT_ID && priceRule.rules.yesProductIds && priceRule.rules.yesProductIds.indexOf(PRODUCT_ID) === -1) {
			continue
		}
		if (criteria.eventOccupationPercent !== undefined && criteria.eventOccupationPercent > priceRule.maxOccupation) {
			continue
		}
		if (criteria.eventOccupationPercent !== undefined && criteria.eventOccupationPercent < priceRule.minOccupation) {
			continue
		}
		if (priceRule.rules.noDts) {
			const ruleNotFulfilled = priceRule.rules.noDts.some((toExclude) => {
				// v -> yyyy-mm-dd || yyyy-mm-dd...yyyy-mm-dd
				const datesToExclude = toExclude.split('...')
				if (datesToExclude.length === 2)
					return (
						new Date(BOOKING_DATE_ISO).getTime() >= new Date(datesToExclude[0]).getTime() &&
						new Date(BOOKING_DATE_ISO).getTime() <= new Date(datesToExclude[1]).getTime()
					)
				else return toExclude === BOOKING_DATE_ISO
			})

			if (ruleNotFulfilled) {
				continue
			}
		}
		fulfilledPriceRules.push(priceRule)
		if (priceRule.cumulative === 0) break
	}

	return fulfilledPriceRules.map((priceRule) => {
		return {
			id: priceRule.id,
			discount: priceRule.discount,
			promotionalText: priceRule.promotionalText,
			visible: priceRule.visible,
			isSpecial: priceRule.isSpecial,
		}
	})
}

export const computeRect = (rect: Rect, point: TableObject, stage?: Stage) => {
	let oy = point.style === 'v' ? point.pax * 50 : 50
	let ox = point.style === 'h' ? point.pax * 50 : 50

	rect.lt.x = Math.min(rect.lt.x, point.x, stage?.x ?? point.x)
	rect.lt.y = Math.min(rect.lt.y, point.y, stage?.y ?? point.y)

	rect.lb.x = Math.min(rect.lb.x, point.x, stage?.x ?? point.x)
	rect.lb.y = Math.max(rect.lb.y, point.y + oy, stage ? stage.y + stage.height : point.y + oy)

	rect.rt.x = Math.max(rect.rt.x, point.x + ox, stage ? stage.x + stage.width : point.x + ox)
	rect.rt.y = Math.min(rect.rt.y, point.y, stage?.y ?? point.y)

	rect.rb.x = Math.max(rect.rb.x, point.x + ox, stage ? stage.x + stage.width : point.x + ox)
	rect.rb.y = Math.max(rect.rb.y, point.y + oy, stage ? stage.y + stage.height : point.y + oy)

	rect.width = rect.rt.x - rect.lt.x
	rect.height = rect.rb.y - rect.rt.y
}

export const modifiersDidMatch = (shortcut: Shortcut, event: KeyboardEvent) => {
	let matches = 0
	if (!!shortcut.modifiers?.altKey === event.altKey) matches += 1
	if (!!shortcut.modifiers?.ctrlKey === event.ctrlKey) matches += 1
	if (!!shortcut.modifiers?.shiftKey === event.shiftKey) matches += 1
	if (!!shortcut.modifiers?.metaKey === event.metaKey) matches += 1
	return matches === 4
}

export const processTableLayout = (tables: TableObject[], h: number, w: number, d = 0, k = 1, venue: SafeVenueData) => {
	log('PROCESS TABLE LAYOUT EXECUTED')
	let maxes: ScoreMaxes = {}
	let min = 10000,
		max = -10000
	let rects = {
		cv: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zv: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		za: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		za2: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zb: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zc: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
	}

	let processedTables = tables.map((table) => {

		//calc table score maxes, col, side, row

		table.x += (TABLES_OVERRIDES as any)[venue.id]?.[table.id]?.x ?? 0
		table.y += (TABLES_OVERRIDES as any)[venue.id]?.[table.id]?.y ?? 0

		const {side, col, row} = getTableVars(table.name)
		maxes[side] = maxes[side] ?? {col: {}, row: {max: 0, min: 100}}
		maxes[side]!.col[row] = maxes[side]!.col[row] ?? {max: 0, min: 100}
		maxes[side]!.col[row].max = Math.max(maxes[side]!.col[row].max, col)
		maxes[side]!.row.max = Math.max(maxes[side]!.row.max, row)
		maxes[side]!.col[row].min = Math.min(maxes[side]!.col[row].min, col)
		maxes[side]!.row.min = Math.min(maxes[side]!.row.min, row)

		computeRect(rects.cv, table, venue.stage)
		computeRect(rects[table.zone], table)

		return {...table, col, side, row}

	}).map(table => ({...table, score: computeTableScore(table, maxes)}))

	return { appliedRotation: d, rects, processedTables }
}

export const applyVenueStyles = (venueData: SafeVenueData) => {


	if (venueData.id === 2) {
		// overwrite venue 2 colors till accepted in rumba
		document.documentElement.style.setProperty('--brand-zone-vip', "#E94464")
		document.documentElement.style.setProperty('--brand-zone-a', "#00a873")
		document.documentElement.style.setProperty('--brand-zone-b', "#447EE9")
		document.documentElement.style.setProperty('--brand-zone-c', "#DEA215")
		document.documentElement.style.setProperty('--brand', "#E94464")
		console.log(venueData.id)
		
	} else {
		document.documentElement.style.setProperty('--brand-zone-vip', venueData.colors.zv)
		document.documentElement.style.setProperty('--brand-zone-a', venueData.colors.za)
		document.documentElement.style.setProperty('--brand-zone-b', venueData.colors.zb)
		document.documentElement.style.setProperty('--brand-zone-c', venueData.colors.zc)
		document.documentElement.style.setProperty('--brand', venueData.colors.brand)
	}

}

type Radii =
	| {
			topLeft?: number
			topRight?: number
			bottomRight?: number
			bottomLeft?: number
	  }
	| number
const drawRoundedRect = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	radii: Radii
) => {
	const radius = {
		topLeft: typeof radii === 'number' ? radii : radii.topLeft || 0,
		topRight: typeof radii === 'number' ? radii : radii.topRight || 0,
		bottomRight: typeof radii === 'number' ? radii : radii.bottomRight || 0,
		bottomLeft: typeof radii === 'number' ? radii : radii.bottomLeft || 0,
	}
	ctx.beginPath()
	ctx.moveTo(x + radius.topLeft, y)
	ctx.lineTo(x + width - radius.topRight, y)
	ctx.arcTo(x + width, y, x + width, y + radius.topRight, radius.topRight)
	ctx.lineTo(x + width, y + height - radius.bottomRight)
	ctx.arcTo(x + width, y + height, x + width - radius.bottomRight, y + height, radius.bottomRight)
	ctx.lineTo(x + radius.bottomLeft, y + height)
	ctx.arcTo(x, y + height, x, y + height - radius.bottomLeft, radius.bottomLeft)
	ctx.lineTo(x, y + radius.topLeft)
	ctx.arcTo(x, y, x + radius.topLeft, y, radius.topLeft)
	ctx.closePath()
}

export const tableLayoutToPng = async (
	layout: ProcessedTableLayout,
	venueData: SafeVenueData,
	booking: BookingData,
	mode?: 1 | 2 | 3 //1 seats disposition | 2 availability | 3 booked seats
): Promise<string | undefined> => {

	try {

		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		if (!ctx) throw new Error('ctx is undefined')
		
		const access1 = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><g clipPath="url(#clip0_770_4767)"><path d="M8.90398 17.9912C13.775 17.9912 17.808 13.9079 17.808 8.99559C17.808 4.07447 13.7662 0 8.89524 0C4.03297 0 0 4.07447 0 8.99559C0 13.9079 4.04171 17.9912 8.90398 17.9912ZM8.90398 16.4919C4.7837 16.4919 1.49273 13.1583 1.49273 8.99559C1.49273 4.83292 4.77497 1.49926 8.89524 1.49926C13.0155 1.49926 16.324 4.83292 16.324 8.99559C16.324 13.1583 13.0243 16.4919 8.90398 16.4919Z" fill="#fff"/><path d="M8.89529 6.0147C7.65572 6.0147 5.89237 5.80303 5.04566 5.67073C4.9671 5.66195 4.89724 5.63546 4.80995 5.63546C4.59171 5.63546 4.36475 5.82069 4.36475 6.13817C4.36475 6.37629 4.51315 6.57913 4.73138 6.64084C5.01944 6.72904 7.00104 6.95833 7.25416 7.00243C7.49859 7.05536 7.64698 7.26703 7.65572 7.56685C7.66446 8.02547 7.65572 9.48947 7.54224 10.1597C7.43751 10.8212 6.67805 14.0049 6.64314 14.146C6.5471 14.49 6.75662 14.7898 7.09704 14.7898C7.34147 14.7898 7.50733 14.6575 7.60333 14.3224C7.75176 13.6786 8.3017 11.4738 8.47631 10.9446C8.58104 10.5566 8.67709 10.4243 8.89529 10.4243C9.10481 10.4243 9.20081 10.5566 9.32302 10.9446C9.47146 11.4738 10.0388 13.6786 10.1873 14.3224C10.2746 14.6575 10.4491 14.7898 10.6936 14.7898C11.034 14.7898 11.2348 14.49 11.1388 14.146C11.1126 14.0049 10.3444 10.8212 10.2396 10.1597C10.1436 9.48947 10.1436 8.02547 10.1436 7.56685C10.1436 7.26703 10.2833 7.05536 10.5364 7.00243C10.7809 6.95833 12.7711 6.72904 13.0505 6.64084C13.2774 6.57913 13.4259 6.37629 13.4259 6.13817C13.4259 5.82069 13.1901 5.63546 12.9719 5.63546C12.8934 5.63546 12.8148 5.66195 12.7362 5.67073C11.8982 5.80303 10.1436 6.0147 8.89529 6.0147ZM8.89529 5.43261C9.5238 5.43261 10.0301 4.91229 10.0301 4.28613C10.0301 3.65115 9.5238 3.13963 8.89529 3.13963C8.26679 3.13963 7.7605 3.65115 7.7605 4.28613C7.7605 4.91229 8.26679 5.43261 8.89529 5.43261Z" fill="#fff"/></g><defs><clipPath id="clip0_770_4767"><rect width="18" height="18" fill="#fff"/></clipPath></defs></svg>`
		const access2 = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M14.3311 4.15352C15.2716 4.15352 16.0256 3.42803 16.0256 2.52304C16.0256 1.62552 15.2716 0.900024 14.3311 0.900024C13.3905 0.900024 12.6365 1.62552 12.6365 2.52304C12.6365 3.42803 13.3905 4.15352 14.3311 4.15352ZM11.4706 17.3171C14.8596 17.3171 17.1215 15.1406 17.1215 11.8797C17.1215 10.1669 16.2743 9.16462 14.6886 9.16462H13.647C13.4683 9.16462 13.3517 9.0525 13.4216 8.84303L14.1367 6.75632C14.4632 5.79897 13.9968 4.93137 12.8931 4.66211L9.68284 3.88427C9.37192 3.80947 9.13874 3.86183 8.85891 4.01889L5.74197 5.70921C5.37664 5.91115 5.29114 6.42723 5.54765 6.75632C5.79638 7.09288 6.23944 7.15272 6.63586 6.95826L9.49629 5.49979L11.1675 5.92611C11.4939 6.01587 11.6183 6.23277 11.5095 6.52446L10.6311 8.89541C10.2269 10.0098 10.911 10.9597 12.0303 10.9597H14.9218C15.5203 10.9597 15.9401 11.3037 15.9401 11.8797C15.9401 14.46 14.16 16.1802 11.4784 16.1802C8.79673 16.1802 7.00118 14.46 7.00118 11.8797C7.00118 10.9896 7.27324 10.2117 7.72407 9.56104C8.2293 8.8356 7.2266 8.22974 6.69804 8.9926C6.15393 9.79295 5.8197 10.7727 5.8197 11.8797C5.8197 15.1406 8.07385 17.3171 11.4706 17.3171ZM17.1448 17.3171C17.619 17.3171 17.9999 16.9506 17.9999 16.4944C17.9999 16.0382 17.619 15.6716 17.1448 15.6716C16.6707 15.6716 16.2898 16.0382 16.2898 16.4944C16.2898 16.9506 16.6707 17.3171 17.1448 17.3171Z" fill="#fff"/></svg>`
		const promises: Promise<void>[] = []
		//padding, rotation, table padding, stage padding, table size, font size, border radius, icon size, --grayscale-80, cv
		const p = 40,
			rt = layout.appliedRotation,
			tp = 5,
			sp = 20,
			ts = 50,
			fs = 17,
			r = 5,
			is = 20,
			grey = '#484A4C',
			cv = layout.rects.cv
	
		const applyRotation = (x: number, y: number, w: number, h: number): [number, number, number, number] => {
			let nx = x,
				ny = y,
				nh = h,
				nw = w
	
			if (rt === 90) {
				;[nw, nh] = [h, w]
				;[nx, ny] = [-y + cv.height - h + p * 2, x]
			} else if (rt === 180) {
				nx = -x + cv.width - w + p * 2
				ny = -y + cv.height - h + p * 2
			} else if (rt === 270) {
				;[nw, nh] = [h, w]
				;[nx, ny] = [y, -x + cv.width - w + p * 2]
			}
	
			return [nx, ny, nw, nh]
		}
	
		const [cvx, cvy, cvw, cvh] = applyRotation(cv.lt.x, cv.lt.y, cv.width, cv.height)
		canvas.height = cvh + p * 2
		canvas.width = cvw + p * 2
	
		//----------------- draw rules
		// ctx.strokeStyle = 'red'
		// ctx.lineWidth = 1
		// Object.values(layout.rects).forEach(rect => {ctx.strokeRect(...applyRotation(rect.lt.x + p - cv.lt.x, rect.lt.y + p - cv.lt.y, rect.width, rect.height))})
	
		//---------------- draw tables
		ctx.font = `500 ${fs}px Work Sans`
		ctx.lineWidth = 3
	
		layout.processedTables.forEach((table) => {
			const o = table.style
			const fi = 0
			const li = table.pax - 1
			const zn = getZoneName('short', table.zone, booking.journey)
			const isInBooking = booking.tables.find((bkTable) => bkTable.id === table.id)
	
			for (let i = 0; i < table.pax; i++) {
				const stx = (table.style === 'h' ? table.x + ts * i : table.x) - cv.lt.x + p
				const sty = (table.style === 'v' ? table.y + ts * i : table.y) - cv.lt.y + p
				//rotated coords & dimensions
				const [stnx, stny, stnw, stnh] = applyRotation(stx, sty, ts, ts)
				const radii: Radii = {}
	
				if (
					//apply border radius
					(i === fi && o === 'h' && rt === 0) ||
					(i === li && o === 'v' && rt === 90) ||
					(i === li && o === 'h' && rt === 180) ||
					(i === fi && o === 'v' && rt === 270)
				) {
					radii.topLeft = r
					radii.bottomLeft = r
				} else if (
					(i === fi && o === 'v' && rt === 0) ||
					(i === fi && o === 'h' && rt === 90) ||
					(i === li && o === 'v' && rt === 180) ||
					(i === li && o === 'h' && rt === 270)
				) {
					radii.topLeft = r
					radii.topRight = r
				}
	
				if (
					//apply border radius
					(i === li && o === 'h' && rt === 0) ||
					(i === fi && o === 'v' && rt === 90) ||
					(i === fi && o === 'h' && rt === 180) ||
					(i === li && o === 'v' && rt === 270)
				) {
					radii.topRight = r
					radii.bottomRight = r
				} else if (
					(i === li && o === 'v' && rt === 0) ||
					(i === li && o === 'h' && rt === 90) ||
					(i === fi && o === 'v' && rt === 180) ||
					(i === fi && o === 'h' && rt === 270)
				) {
					radii.bottomLeft = r
					radii.bottomRight = r
				}
	
				//fill grey if availability mode & not available || booked-seats & not booked
				if ((mode === 2 && table.occupied > 0) || (mode === 3 && !isInBooking)) {
					ctx.strokeStyle = grey
					ctx.fillStyle = grey
				} else {
					ctx.strokeStyle = venueData.colors[table.zone]
					ctx.fillStyle = venueData.colors[table.zone]
				}
	
				//draw table
				ctx.globalAlpha = 0.5 //table bg opacity
				drawRoundedRect(ctx, stnx, stny, stnw, stnh, radii)
				ctx.fill()
				ctx.globalAlpha = 1
				ctx.stroke()
	
				if (
					((mode === 3 && isInBooking) || mode !== 3) &&
					((rt === 0 && i === fi) ||
						(rt === 180 && i === li) ||
						(rt === 90 && i === li && o === 'v') ||
						(rt === 90 && i === fi && o === 'h') ||
						(rt === 270 && i === li && o === 'h') ||
						(rt === 270 && i === fi && o === 'v'))
				) {
					//place zone name (zn)
					//change zn to table.name to display table name
					ctx.fillStyle = 'white'
					ctx.fillText(table.name, stnx + tp, stny + 17)
				}
	
				if (
					((mode === 3 && isInBooking) || mode !== 3) &&
					table.access > 0 &&
					((rt === 0 && i === li) ||
						(rt === 180 && i === fi) ||
						(rt === 90 && i === fi && o === 'v') ||
						(rt === 90 && i === li && o === 'h') ||
						(rt === 270 && i === fi && o === 'h') ||
						(rt === 270 && i === li && o === 'v'))
				) {
					//place access icon
					const addIcon = async () =>
						new Promise<void>((resolve, reject) => {
							const blob = new Blob([table.access === 1 ? access1 : access2], { type: 'image/svg+xml' })
							const url = URL.createObjectURL(blob)
							const icon = new Image()
							icon.src = url
							icon.onload = () => {
								ctx.drawImage(icon, stnx + stnw - (is + tp), stny + stnh - (is + tp), is, is)
								URL.revokeObjectURL(url)
								resolve()
							}
	
							icon.onerror = reject
						})
	
					promises.push(addIcon())
				}
			}
		})
	
		//draw stage
		ctx.globalAlpha = 1
		ctx.fillStyle = grey
		const [stnx, stny, stnw, stnh] = applyRotation(
			venueData.stage.x - cv.lt.x + p,
			venueData.stage.y - cv.lt.y + p,
			venueData.stage.width,
			venueData.stage.height
		)
		drawRoundedRect(ctx, stnx, stny, stnw, stnh, r)
		ctx.fill()
		ctx.fillStyle = 'white'
	
		const addLogo = async () =>
			new Promise<void>((resolve, reject) => {
				const logo = new Image()
				logo.crossOrigin = 'anonymous'
				//logo.src = `https://cardamomo.ams3.cdn.digitaloceanspaces.com/bookings/resources/img/logo-venue-${venueData.id}.png`
				logo.src = 'https://cardamomo.ams3.cdn.digitaloceanspaces.com/bookings/resources/img/cardamomo-stage.png'
				logo.onload = () => {
					let scale = Math.min((stnw * 0.5) / logo.width, (stnh - sp * 2) / logo.height)
	
					const lw = logo.width * scale
					const lh = logo.height * scale
					ctx.drawImage(logo, stnx + stnw / 2 - lw / 2, stny + stnh / 2 - lh / 2, lw, lh)
					resolve()
				}
	
				logo.onerror = reject
			})
	
		//comment to not add logo
		// promises.push(addLogo())
	
		//place stage text
		ctx.textAlign = 'right'
		ctx.fillText('STAGE', stnx + stnw - sp, stny + stnh - sp)
	
		await Promise.all(promises)
		return canvas.toDataURL('image/png').split(',')[1]

	} catch (error) {

		console.error(`Could not generate venue pic, using default image in backend`, error)
		return undefined
	}
}

/**
 * returns how many adults seats can be replcaded with child seats
 * in compliance with the current rule
 */
export const calcMaxChildren = (adults: number) => {
	let a = adults * CHILDREN_PER_ADULT,
		b = 0

	while (true) {
		a -= CHILDREN_PER_ADULT + 1
		if (a < 0) break
		b += 1
	}

	return b
}

export const getTableVars = (str: string) => {
	let side: 'right' | 'front' | 'left', row: number, col: number

	switch (booking.venueId) {
		case 1:
			let row_str = str.substring(0, str.length - 1)
			let col_str = str.substring(str.length - 1)

			if (isNaN(Number(row_str))) {
				side = 'right'
				row = row_str.charCodeAt(0) - 64
			} else {
				side = 'front'
				row = Number(row_str)
			}

			col = Number(col_str)

			break
		case 2:
			let [str0, str1, str2] = str.split('')
			side = str0 === '3' ? 'left' : str0 === '2' ? 'front' : 'right'
			row = Number(str1)
			col = Number(str2)

			break
		case 3:
			side = 'front'
			row = Number(str.substring(0, str.length - 1))
			col = Number(str.substring(str.length - 1))
			break
	}

	return { side, row, col }
}

export const mapShifts = (shift: Shift, priceRules: PriceRule[], booking: BookingData): Shift & ComputedShiftData => {

	//when calling rumba.getshifts, 
	// updateShifts(response.shifts.map(shift => mapShifts(
		// shift, 
		// updatedPriceRules,
		// booking.getBookingDataByReference()
	// )))

	const matches = findPriceRuleMatches({
		journey: booking.journey,
		bookingDate: booking.date, 
		shiftId: shift.id, 
		eventId: shift.eventId
	}, priceRules)

	const visibleDiscountIsPresent = !!matches.find(match => match.discount > 0 && match.visible === 1)
	const specialEventText = matches.find(match => match.isSpecial === 1)?.promotionalText ?? ''        

	return {...shift, discount: visibleDiscountIsPresent, specialEvent: specialEventText}
}

export const computeTableScore = (table: TableObject, maxes: ScoreMaxes): number => {
	// Valores por defecto
	let cmax = 0, rmax = 0, cmin = 0, rmin = 0, rmult = 1;
  
	// Configuración según el lado
	const sideConfig = maxes[table.side];
	if (sideConfig) {
	  cmax = sideConfig.col[table.row].max ?? 0;
	  cmin = sideConfig.col[table.row].min ?? 0;
	  rmax = sideConfig.row.max ?? 0;
	  rmin = sideConfig.row.min ?? 0;
  
	  // Ajustar rmult según el lado
	  rmult = table.side === 'front' ? 2 : 1.5;
	}
  
	// Calcular puntuaciones de fila y columna
	const rowScore = (rmax + rmin) - table.row * rmult;
	const colScore = cmax - Math.abs((table.col) - (cmax + cmin) / 2);
  
	// Retornar la suma de puntuaciones
	return rowScore + colScore;
  };
  
  export const getTimeString = (date: string | number | Date) => {
	const dateObject = new Date(date)
	return `${dateObject.getHours().toString().padStart(2, '0')}:${dateObject.getMinutes().toString().padStart(2, '0')}`
}